<template>
  <v-container fluid>
    <v-card>
      <v-card-title
        >{{ $t('restaurant.menu.menu.prices') }}
        <RestaurantTierChip class="ml-2" :tier="1"
      /></v-card-title>
      <v-card-text>
        <v-skeleton-loader
          type="paragraph"
          v-if="loadingFilters || !restaurantSettings"
        />
        <template v-else>
          <collapsable-toolbar
            v-if="menus !== null && menus.length > 0"
            v-model="filtersOpen"
            class="filters-toolbar"
          >
            <v-form @submit.prevent v-model="filtersValid" style="width: 100%">
              <v-row>
                <v-col cols="auto" v-if="menusInUse.length > 1">
                  <div class="d-flex align-center">
                    <span class="mr-2">Menús</span>
                    <v-checkbox
                      dense
                      label="Todos"
                      hide-details
                      v-model="filters.allMenus"
                    />
                  </div>
                  <v-select
                    v-if="!filters.allMenus"
                    :items="menusInUse"
                    item-text="name"
                    item-value="id"
                    hide-details
                    v-model="selectedMenuIds"
                    placeholder="Seleccione los menús"
                    :menu-props="{ offsetY: true }"
                    filled
                    chips
                    :rules="$rule('required')"
                    deletable-chips
                    multiple
                  />
                </v-col>
                <v-col cols="auto">
                  <div class="d-flex align-center">
                    <span class="mr-2">Categorías</span>
                    <v-checkbox
                      dense
                      label="Todas"
                      hide-details
                      v-model="filters.allCategories"
                      :disabled="
                        !filters.allMenus &&
                        menusInUse.length > 1 &&
                        !selectedMenuIds.length
                      "
                    />
                  </div>
                  <v-autocomplete
                    :items="availableCategories"
                    v-if="!filters.allCategories"
                    item-text="name"
                    item-value="id"
                    v-model="selectedCategories"
                    :disabled="
                      !filters.allMenus &&
                      menusInUse.length > 1 &&
                      !selectedMenuIds.length
                    "
                    return-object
                    hide-details
                    label="Seleccione las categorías"
                    single-line
                    filled
                    chips
                    :rules="$rule('required')"
                    deletable-chips
                    multiple
                  />
                </v-col>
              </v-row>
              <v-row align="center" justify="center">
                <v-col class="pt-0" cols="auto">
                  <v-btn
                    @click="loadItems"
                    :loading="loadingItems"
                    color="success"
                    depressed
                    >Traer items</v-btn
                  >
                </v-col>
              </v-row>
            </v-form @submit.prevent>
          </collapsable-toolbar>
        </template>
        <template v-if="loadingItems">
          <v-skeleton-loader type="paragraph" />
        </template>
        <template v-else-if="items">
          <v-row align="center" class="filters-nav">
            <v-col cols="auto">
              <v-checkbox
                :input-value="
                  visibleSelectedItems.length === filteredItems.length
                "
                @click="toggleSelectAll"
                label="Seleccionar todo"
                class="pl-3 mt-0"
                hide-details
              />
            </v-col>
            <v-col cols="auto">
              <v-text-field
                placeholder="Filtrar por nombre"
                v-model="filters.name"
                single-line
                prepend-inner-icon="mdi-magnify"
                clearable
                hide-details
                class="mt-0"
                outlined
                dense
              />
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <div class="d-flex align-center tw-flex-wrap tw-gap-2">
                <span
                  >{{ visibleSelectedItems.length }} items seleccionados</span
                >
                <v-btn
                  type="text"
                  :disabled="!visibleSelectedItems.length"
                  @click="deselect"
                  small
                  >Deseleccionar</v-btn
                >
                <v-btn
                  color="info"
                  :disabled="!visibleSelectedItems.length"
                  @click="clear"
                  small
                  >Limpiar</v-btn
                >
                <v-btn
                  color="success"
                  :disabled="!visibleSelectedItems.length"
                  @click="increaseAmount"
                  small
                  >Aumentar $</v-btn
                >
                <v-btn
                  color="success"
                  :disabled="!visibleSelectedItems.length"
                  @click="increasePercent"
                  small
                  >Aumentar %</v-btn
                >
              </div>
            </v-col>
          </v-row>
          <v-data-table
            v-model="selectedItems"
            :headers="headers"
            :items="filteredItems"
            dense
            :item-class="getItemClass"
            show-select
            fixed-header
            :mobile-breakpoint="0"
            hide-default-footer
            :items-per-page="-1"
            class="items-table elevation-0 v-data-table--striped v-data-table--orange mt-5"
          >
            <template #item.menuId="{ item }"
              ><span class="grey--text">{{ item.menu.name }}</span></template
            >
            <template #item.category.name="{ item }"
              ><span class="grey--text">{{
                item.category.name
              }}</span></template
            >
            <template #item.price="{ item }">
              <template v-if="!item.priceVariants">
                {{ currency.symbol }}{{ item.price }}
              </template>
              <div class="price-variants" v-else>
                <div
                  class="price-variant d-flex align-center justify-space-between"
                  v-for="p of item.priceVariants.optionItems"
                  :key="p.id"
                >
                  <span class="cost">{{ currency.symbol }}{{ p.cost }}</span>
                  <span class="name grey--text">{{ p.title }}</span>
                </div>
              </div>
            </template>
            <template #item.newPrice="{ item }">
              <template v-if="!item.priceVariants">
                <v-text-field
                  class="new-price-input v-text-field--seamless v-text-field--number"
                  dense
                  v-model="item.newPrice"
                  type="number"
                  solo
                  :prefix="currency.symbol"
                  :placeholder="item.price + ''"
                  flat
                  hide-details
                  single-line
                />
              </template>
              <div class="" v-else>
                <v-text-field
                  v-for="p of item.priceVariants.optionItems"
                  :key="p.id"
                  class="new-price-input v-text-field--seamless v-text-field--number"
                  dense
                  v-model="p.newCost"
                  type="number"
                  solo
                  :prefix="currency.symbol"
                  :placeholder="p.cost + ''"
                  flat
                  hide-details
                  single-line
                />
              </div>
            </template>
            <template #item.discountedPrice="{ item }">
              <template v-if="!item.priceVariants">
                <span v-if="item.discountedPrice === null" class="grey--text"
                  >-</span
                >
                <span v-else
                  >{{ currency.symbol }}{{ item.discountedPrice }}</span
                >
              </template>
              <div class="price-variants" v-else>
                <div
                  class="price-variant d-flex align-center justify-space-between"
                  v-for="p of item.priceVariants.optionItems"
                  :key="p.id"
                >
                  <span v-if="p.discountedCost === null" class="grey--text"
                    >-</span
                  >
                  <span v-else
                    >{{ currency.symbol }}{{ p.discountedCost }}</span
                  >
                </div>
              </div>
            </template>
            <template #item.newDiscountedPrice="{ item }">
              <template v-if="!item.priceVariants">
                <span v-if="item.discountedPrice === null" class="grey--text"
                  >-</span
                >
                <v-text-field
                  v-else
                  class="new-price-input v-text-field--seamless v-text-field--number"
                  dense
                  type="number"
                  v-model="item.newDiscountedPrice"
                  solo
                  :prefix="currency.symbol"
                  :placeholder="item.discountedPrice + ''"
                  flat
                  hide-details
                  single-line
                />
              </template>
              <div class="" v-else>
                <div v-for="p of item.priceVariants.optionItems" :key="p.id">
                  <span v-if="p.discountedCost === null" class="grey--text"
                    >-</span
                  >
                  <v-text-field
                    v-else
                    class="new-price-input v-text-field--seamless v-text-field--number"
                    dense
                    type="number"
                    v-model="p.newDiscountedCost"
                    solo
                    :prefix="currency.symbol"
                    :placeholder="p.discountedCost + ''"
                    flat
                    hide-details
                    single-line
                  />
                </div>
              </div>
            </template>

            <template #item.lastPriceChangeAt="{ item }">
              <span v-if="!item.lastPriceChangeAt">-</span>
              <timeago v-else :datetime="item.lastPriceChangeAt"></timeago>
            </template>
          </v-data-table>
        </template>
        <v-row justify="end" align="center">
          <v-col
            cols="auto"
            v-if="changedItems.length"
            class="font-weight-medium black--text"
          >
            Ha hecho cambios en {{ changedItems.length }} items
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              @click="preview"
              :disabled="!changedItems.length"
              >Revisar y aplicar cambios</v-btn
            >
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import ItemsService from '../../api/services/items.service';
  import MenusService from '../../api/services/menus.service';
  import { IBranch } from '../../interfaces/IBranch';
  import { IMenu } from '../../interfaces/IMenu';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import InputModal from '../modals/InputModal.vue';
  import { IItemResumed } from '../../interfaces/IItemResumed';
  import { vueData } from '../../VueData';
  import CategoriesService from '../../api/services/categories.service';
  import { ICategory } from '../../interfaces/ICategory';
  import RestaurantsService, {
    IRestaurantSettings,
  } from '../../api/services/restaurants.service';
  import CollapsableToolbar from '../widgets/CollapsableToolbar.vue';
  import { AlertType } from '../../interfaces/IAlert';
  import { ICurrency } from '../../interfaces/ICurrency';
  import { IItem } from '../../interfaces/IItem';
  import { showScrollableModal } from '../../lib/modals';
  import PricesPreview from '../modals/PricesPreview.vue';
  import { IOptionItem } from '../../interfaces/IOptionItem';
  import { IOption } from '../../interfaces/IOption';
  import IncreasePercentageModal from '../modals/IncreasePercentageModal.vue';
  import IRoundingData from '../../interfaces/IRoundingData';
  import { parseNumInput, round, round2 } from '../../utils';
  import { IItemResumedPriceChange } from '../../interfaces/PriceChanges';
import RestaurantTierChip from '../partials/RestaurantTierChip.vue';

  @Component({
    components: { CollapsableToolbar, RestaurantTierChip },
  })
  export default class PriceAdmin extends Vue {
    @Prop() restaurant: IRestaurant;
    @Prop() branch: IBranch;
    @Prop() currency: ICurrency;

    itemsService = new ItemsService();
    menusService = new MenusService();
    categoriesService = new CategoriesService();
    restaurantsService = new RestaurantsService();

    restaurantSettings: IRestaurantSettings = null;

    loadingFilters = true;
    menus: IMenu[] = null;
    selectedMenuIds: number[] = [];

    categories: ICategory[] = null;
    selectedCategories: ICategory[] = [];

    items: IItemResumedPriceChange[] = null;
    selectedItems: IItemResumedPriceChange[] = [];

    loadingItems = false;
    filtersOpen = true;
    filtersValid = false;

    filters = {
      allCategories: true,
      allMenus: true,
      name: null as string | null,
    };

    async created() {
      this.loadSettings();
      this.loadFilters();
      //await this.loadItems();
      // await this.loadSettings();
    }

    async loadSettings() {
      this.restaurantSettings = await this.restaurantsService.getSettings(
        this.restaurant.id
      );
    }

    async loadFilters() {
      await this.loadMenus();
      await this.loadCategories();
      this.loadingFilters = false;
    }

    async loadItems() {
      if (!this.filters.allMenus && !this.selectedMenuIds.length) {
        vueData.addAlert('Debe seleccionar algún menú', AlertType.Error);
        return;
      }

      if (!this.filters.allCategories && !this.selectedCategories.length) {
        vueData.addAlert('Debe seleccionar alguna categoría', AlertType.Error);
        return;
      }

      this.filters.name = null;
      this.selectedItems = [];
      this.loadingItems = true;
      this.filtersOpen = false;

      try {
        const items = (await ItemsService.getAllResumed(
            {
              restaurantId: this.restaurant.id!,
              categoryIds: this.filters.allCategories
                ? this.availableCategories.map((c) => c.id)
                : this.selectedCategories.map((c) => c.id),
              menuIds: this.filters.allMenus
                ? this.menusInUse.map((m) => m.id ?? 0)
                : this.selectedMenuIds
            }
          )
        ) as IItemResumedPriceChange[];

        for (const i of items) {
          i.menu = this.menusById[i.menuId ?? 0];
          i.category = this.categoriesById[i.categoryId ?? 0];
          i.newPrice = i.newDiscountedPrice = null;
          if (i.priceVariants) {
            for (const oI of i.priceVariants.optionItems) {
              oI.newCost = null;
              oI.newDiscountedCost = null;
            }
          }
        }

        console.log(items[0].name);
        items.sort((a, b) => {
          const _a = ''.concat(a.category.name, '|', a.name);
          const _b = ''.concat(b.category.name, '|', b.name);
          return _a > _b ? 1 : -1;
        });
        console.log(items[0].name);

        this.items = items;

        Vue.nextTick(() => {
          window.scrollTo(0, 0);
        });
      } catch (e) {
        console.error(e);
        vueData.serverError(e);
      } finally {
        this.loadingItems = false;
      }
    }

    getItemClass(item: IItemResumedPriceChange) {
      let price: number, newPrice: number;

      if (!item.priceVariants) {
        if (
          (item.discountedPrice !== null && item.newDiscountedPrice === null) ||
          (item.discountedPrice === null && item.newPrice === null)
        )
          return undefined;
        price = item.discountedPrice ?? item.price;
        newPrice = item.newDiscountedPrice ?? item.newPrice;
      } else {
        let avgCost: number = 0,
          avgNewCost: number = 0;

        for (const oI of item.priceVariants.optionItems) {
          const cost = oI.cost ?? 0,
            hasDiscountedCost = oI.discountedCost !== null;
          avgCost += oI.discountedCost ?? cost;
          if (hasDiscountedCost)
            avgNewCost += parseNumInput(
              oI.newDiscountedCost ?? oI.discountedCost
            );
          else avgNewCost += parseNumInput(oI.newCost ?? cost);
        }

        price = avgCost;
        newPrice = avgNewCost;
      }

      if (newPrice > price) return 'bigger';
      else if (newPrice < price) return 'smaller';
      else return undefined;
    }

    get filteredItems() {
      if (!this.items) return null;

      if (!this.filters?.name?.trim()) return this.items;

      return this.items.filter((i) =>
        i.name.toLowerCase().includes(this.filters.name.toLowerCase())
      );
    }

    get visibleSelectedItems() {
      if (!this.filters?.name?.trim()) return this.selectedItems;

      return this.selectedItems.filter((i) =>
        i.name.toLowerCase().includes(this.filters.name.toLowerCase())
      );
    }

    async loadMenus() {
      const menus = await this.menusService.getAll(this.restaurant.id);

      menus.unshift({
        id: 0,
        name: 'Menú por defecto',
        active: true,
        deletedAt: null,
      } as IMenu);

      this.menus = menus;
    }

    async loadCategories() {
      const p = [],
        allCategories: ICategory[] = [];

      const createPromise = (menuId?) => {
        p.push(
          new Promise<void>(async (resolve) => {
            const categories = await RestaurantsService.getCategories(
              this.restaurant.id,
              menuId
            );
            allCategories.push(...categories);
            resolve();
          })
        );
      };

      createPromise();

      for (const menu of this.menus) {
        createPromise(menu.id);
      }

      this.categories = allCategories;
    }

    toggleSelectAll() {
      if (this.selectedItems.length < this.filteredItems.length)
        this.selectedItems = [...this.filteredItems];
      else this.selectedItems = [];
    }

    preview() {
      showScrollableModal({
        modalInstance: this.$modal,
        component: PricesPreview,
        props: {
          currency: this.currency,
          items: this.changedItems,
          restaurantSettings: this.restaurantSettings,
          menus: this.menus,
        },
        events: {
          saved: () => {
            this.selectedItems = [];
            this.$modal.hide('price-preview');
            this.loadItems();
          },
        },
        width: 1000,
        name: 'price-preview',
      });
    }

    increasePercent() {
      this.$modal.show(
        IncreasePercentageModal,
        {},
        {
          name: 'increase-percent',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        },
        {
          submit: async (amount: number, roundingData: IRoundingData) => {
            const mult = amount / 100 + 1;

            function addAndRound(val: number) {
              const res = val * mult;
              return roundingData !== null
                ? round(res, roundingData.step, roundingData.direction)
                : round2(res);
            }

            for (const item of this.visibleSelectedItems) {
              if (!item.priceVariants) {
                item.newPrice = addAndRound(item.price);
                if (item.discountedPrice !== null)
                  item.newDiscountedPrice = addAndRound(item.discountedPrice);
              } else {
                for (const oI of item.priceVariants.optionItems) {
                  oI.newCost = addAndRound(oI.cost);
                  if (oI.discountedCost !== null)
                    oI.newDiscountedCost = addAndRound(oI.discountedCost);
                }
              }
            }

            this.$modal.hide('increase-percent');
          },
        }
      );
    }

    clear() {
      for (const item of this.visibleSelectedItems) {
        if (!item.priceVariants) {
          item.newPrice = null;
          item.newDiscountedPrice = null;
        } else {
          for (const oI of item.priceVariants.optionItems) {
            oI.newCost = null;
            oI.newDiscountedCost = null;
          }
        }
      }
    }

    deselect() {
      this.selectedItems = [];
    }

    increaseAmount() {
      this.$modal.show(
        InputModal,
        {
          title: 'Ingrese el monto',
          message:
            'El nuevo precio sera el precio original más el monto ingresado. Para reducir, ingrese un número negativo (-10).',
          prefix: '$',
          placeholder: 'Escriba un monto',
          rules: [(v) => /^[\-0-9\.,\-]{1,10}$/.test(v) || 'Solo números'],
          type: 'number',
        },
        {
          name: 'increase-amount',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        },
        {
          submit: async (input: string) => {
            const amount = parseNumInput(input);

            for (const item of this.visibleSelectedItems) {
              if (!item.priceVariants) {
                item.newPrice = item.price + amount;
                if (item.discountedPrice !== null)
                  item.newDiscountedPrice = item.discountedPrice + amount;
              } else {
                for (const oI of item.priceVariants.optionItems) {
                  oI.newCost = oI.cost + amount;
                  if (oI.discountedCost !== null)
                    oI.newDiscountedCost = oI.discountedCost + amount;
                }
              }
            }

            this.$modal.hide('increase-amount');
          },
        }
      );
    }

    @Watch('selectedMenuIds')
    onMenuIdsChanged() {
      this.selectedCategories = this.selectedCategories.filter(
        (c) => this.selectedMenuIds.indexOf(c.menuId ?? 0) > -1
      );
    }

    get changedItems() {
      if (!this.items) return [];
      const changed = [];

      for (const i of this.items) {
        if (!i.priceVariants) {
          if (typeof i.newPrice === 'string')
            i.newPrice = parseNumInput(i.newPrice);
          if (typeof i.newDiscountedPrice === 'string')
            i.newDiscountedPrice = parseNumInput(i.newDiscountedPrice);
          if (
            (i.newPrice !== null && i.newPrice !== i.price) ||
            (i.discountedPrice !== null &&
              i.newDiscountedPrice !== null &&
              i.discountedPrice !== i.newDiscountedPrice)
          )
            changed.push(i);
        } else {
          if (
            i.priceVariants.optionItems.some((oI) => {
              if (typeof oI.newCost === 'string')
                oI.newCost = parseNumInput(oI.newCost);
              if (typeof oI.newDiscountedCost === 'string')
                oI.newDiscountedCost = parseNumInput(oI.newDiscountedCost);
              return (
                (oI.newCost !== null && oI.newCost !== oI.cost) ||
                (oI.discountedCost !== null &&
                  oI.newDiscountedCost !== null &&
                  oI.discountedCost !== oI.newDiscountedCost)
              );
            })
          )
            changed.push(i);
        }
      }

      return changed;
    }

    get headers() {
      const headers = [
        { text: 'Categoría', value: 'category.name' },
        { text: 'Nombre', value: 'name' },
        { text: 'Precio', value: 'price' },
        { text: 'Nuevo precio', value: 'newPrice' },
        { text: 'C/ descuento', value: 'discountedPrice' },
        { text: 'Nuevo c/ descuento', value: 'newDiscountedPrice' },
        { text: 'Último cambio', value: 'lastPriceChangeAt' },
      ];

      if (this.activeMenuIds.length > 1)
        headers.unshift({ text: 'Menú', value: 'menuId' });

      return headers;
    }

    get categoriesById() {
      const catById = {};
      for (const cat of this.categories) {
        catById[cat.id] = cat;
      }
      return catById;
    }

    get menusById() {
      const mById = {};
      mById[0] = { id: 0, name: 'Menú por defecto' };
      for (const m of this.menus) {
        mById[m.id] = m;
      }
      return mById;
    }

    get availableCategories() {
      const menuIds = this.filters.allMenus
        ? this.menusInUse.map((m) => m.id ?? 0)
        : this.selectedMenuIds;
      return this.categories.filter((c) => menuIds.indexOf(c.menuId ?? 0) > -1);
    }

    get activeMenuIds() {
      const menuIds = [];

      if (!this.restaurantSettings.multipleMenus) menuIds.push(0);
      else {
        for (let i = 1; i <= 3; i++) {
          const menuId = this.restaurantSettings[`menu${i}Id`] ?? 0;
          if (menuIds.indexOf(menuId) === -1) menuIds.push(menuId);
        }
      }

      return menuIds;
    }

    get menusInUse() {
      return this.menus.filter((m) => this.activeMenuIds.indexOf(m.id) > -1);
    }
  }
</script>

<style lang="scss" scoped>
  .filters-nav {
    position: -webkit-sticky;
    position: sticky;
    top: 48px;
    background-color: #ffffff;
    z-index: 5;
  }

  .items-table {
    ::v-deep tr {
      td {
        border-bottom: 0 !important;
      }

      &.smaller {
        background-color: #ffe5e5 !important;

        &:nth-of-type(odd) {
          background-color: #ffd4d4 !important;
        }

        &.v-data-table__selected {
          background-color: #ffbdbd !important;
          &:nth-of-type(odd) {
            background-color: #ffb1b1 !important;
          }
        }
      }

      &.bigger {
        background-color: #e9ffe5 !important;

        &:nth-of-type(odd) {
          background-color: #d5ffcf !important;
        }

        &.v-data-table__selected {
          background-color: #b1ffa5 !important;
          &:nth-of-type(odd) {
            background-color: #99ff8a !important;
          }
        }
      }
    }
  }

  .price-variants {
    padding: 4px 0;

    .price-variant {
      height: 30px;

      .name {
      }

      .cost {
        padding-right: 4px;
      }
    }
  }

  .new-price-input {
    padding: 2px 0 !important;

    .v-input__slot {
      background: #f9f9f9 !important;
    }

    .v-text-field__prefix {
      color: #a9a9a9;
    }
  }
</style>

<style lang="scss">
  .v-text-field--seamless {
    font-size: 14px;

    .v-input__control {
      min-height: 18px !important;
    }

    input {
      line-height: 18px !important;
    }
  }

  .v-text-field--number {
    width: 100px !important;
  }
</style>